import { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Plus, Trash, SignOut, Package, Receipt, Printer, CaretDown, CaretUp, ArrowsClockwise, WhatsappLogo, MagnifyingGlass, Copy, ListChecks, PencilSimple, GearSix, X, ArrowUp, ArrowDown, CheckCircle, ArrowLeft } from '@phosphor-icons/react';
import { toast } from 'sonner';
import { loadCategoryConfigs, saveCategoryConfigs, buildFieldsForCategory, buildEmptySelection, buildDefaultSelections, buildSmartLookupSelections, buildBrandMap, buildCatMap, getNextColor, generateCategoryId, AVAILABLE_FIELDS, DEFAULT_CATEGORY_CONFIGS } from '../utils/categoryConfig';

// Reusable dropdown group component
function DropdownGroup({ label, fields, state, onChange, color: labelColor }) {
  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

  // Fetch options for each field
  const [options, setOptions] = useState({});

  const fetchOptions = useCallback(async (fieldKey, endpoint) => {
    try {
      const { data } = await axios.get(`${BACKEND_URL}${endpoint}`);
      setOptions(prev => ({ ...prev, [fieldKey]: data }));
    } catch (e) { console.error(e); }
  }, [BACKEND_URL]);

  useEffect(() => {
    // Fetch brands for the first field
    const firstField = fields[0];
    if (firstField && firstField.fetchUrl) {
      fetchOptions(firstField.key, firstField.fetchUrl);
    }
  }, [fields[0]?.fetchUrl]);

  // Cascade: when a field value changes, fetch next field's options
  useEffect(() => {
    fields.forEach((field, i) => {
      if (i > 0 && field.fetchUrl && field.dependsOn) {
        const allDepsSet = field.dependsOn.every(dep => state[dep]);
        if (allDepsSet) {
          let url = field.fetchUrl;
          field.dependsOn.forEach(dep => {
            url = url.replace(`{${dep}}`, encodeURIComponent(state[dep]));
          });
          fetchOptions(field.key, url);
        }
      }
    });
  }, [state, fields]);

  const handleChange = (key, value, idx) => {
    const newState = { ...state, [key]: value };
    // Clear subsequent fields
    fields.slice(idx + 1).forEach(f => { newState[f.key] = ''; });
    onChange(newState);
    // Clear options for subsequent fields
    const clearedOpts = { ...options };
    fields.slice(idx + 1).forEach(f => { clearedOpts[f.key] = []; });
    setOptions(clearedOpts);
  };

  return (
    <div className="border border-border rounded-sm p-3 bg-white">
      <div className="text-[10px] font-semibold uppercase tracking-wider mb-2" style={{ color: labelColor || '#0066FF' }}>{label}</div>
      <div className={`grid gap-2 ${fields.length === 1 ? 'grid-cols-1' : fields.length === 2 ? 'grid-cols-2' : fields.length === 3 ? 'grid-cols-3' : 'grid-cols-4'}`}>
        {fields.map((field, i) => (
          <div key={field.key}>
            <select
              value={state[field.key] || ''}
              onChange={(e) => handleChange(field.key, e.target.value, i)}
              disabled={i > 0 && field.dependsOn && !field.dependsOn.every(d => state[d])}
              className="flex h-8 w-full rounded-sm border border-input bg-transparent px-2 py-1 text-xs shadow-sm focus:outline-none focus:ring-1 focus:ring-ring disabled:cursor-not-allowed disabled:opacity-50"
              data-testid={`${label.toLowerCase().replace(/[\s\/]/g, '-')}-${field.key}`}
            >
              <option value="">-- {field.label} --</option>
              {(options[field.key] || []).map(opt => <option key={opt} value={opt}>{opt}</option>)}
            </select>
          </div>
        ))}
      </div>
    </div>
  );
}

export default function Dashboard() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
  const codeInputRef = useRef(null);

  // ── Dynamic Category Configuration ──
  const [categoryConfigs, setCategoryConfigs] = useState(() => loadCategoryConfigs());
  const [editingCatId, setEditingCatId] = useState(null);
  const [editingCatName, setEditingCatName] = useState('');
  const [configuringCatId, setConfiguringCatId] = useState(null);
  const [showAddCategory, setShowAddCategory] = useState(false);
  const [newCatName, setNewCatName] = useState('');
  const [newCatProductKey, setNewCatProductKey] = useState('');
  const [newCatFields, setNewCatFields] = useState(['brand']);
  const [confirmDeleteCatId, setConfirmDeleteCatId] = useState(null);

  // Persist category configs to localStorage
  useEffect(() => { saveCategoryConfigs(categoryConfigs); }, [categoryConfigs]);

  // ── Area/Room-based BOQ Partitions ──
  const defaultSelections = buildDefaultSelections(categoryConfigs);
  const [areas, setAreas] = useState([{ id: 1, name: 'GENERAL', selections: JSON.parse(JSON.stringify(defaultSelections)) }]);
  const [activeAreaId, setActiveAreaId] = useState(1);
  const [nextAreaId, setNextAreaId] = useState(2);
  const [editingAreaName, setEditingAreaName] = useState(null);
  const [tempAreaName, setTempAreaName] = useState('');

  const activeArea = areas.find(a => a.id === activeAreaId) || areas[0];
  const sel = activeArea.selections;

  // Generic setter for any category selection
  const updateSel = (key, val) => setAreas(prev => prev.map(a => a.id === activeAreaId ? { ...a, selections: { ...a.selections, [key]: val } } : a));

  // Area management
  const addArea = () => {
    const newName = `AREA ${nextAreaId}`;
    setAreas(prev => [...prev, { id: nextAreaId, name: newName, selections: JSON.parse(JSON.stringify(defaultSelections)) }]);
    setActiveAreaId(nextAreaId);
    setNextAreaId(prev => prev + 1);
    toast.success(`Added area: ${newName}`);
  };
  const renameArea = (areaId, newName) => {
    const oldArea = areas.find(a => a.id === areaId);
    if (!oldArea) return;
    const oldName = oldArea.name;
    setAreas(prev => prev.map(a => a.id === areaId ? { ...a, name: newName.toUpperCase() } : a));
    // Also update items that belong to this area
    setItems(prev => prev.map(item => item.area === oldName ? { ...item, area: newName.toUpperCase() } : item));
    setEditingAreaName(null);
  };
  const removeArea = (areaId) => {
    if (areas.length <= 1) { toast.error('Cannot remove the last area'); return; }
    const areaToRemove = areas.find(a => a.id === areaId);
    if (!areaToRemove) return;
    // Remove items belonging to this area
    setItems(prev => prev.filter(item => item.area !== areaToRemove.name));
    setAreas(prev => prev.filter(a => a.id !== areaId));
    if (activeAreaId === areaId) setActiveAreaId(areas.find(a => a.id !== areaId).id);
    toast.success(`Removed area: ${areaToRemove.name}`);
  };

  const [codeInput, setCodeInput] = useState('');
  const [qtyInput, setQtyInput] = useState('1');
  const [discountInput, setDiscountInput] = useState('0');

  // Insert-between state
  const [insertAfterIdx, setInsertAfterIdx] = useState(null);
  const [insertCode, setInsertCode] = useState('');
  const [insertQty, setInsertQty] = useState('1');
  const [insertDisc, setInsertDisc] = useState('0');
  const insertCodeRef = useRef(null);

  // Edit-in-place state
  const [editingItemIdx, setEditingItemIdx] = useState(null);
  const [editCode, setEditCode] = useState('');

  // Autocomplete state
  const [allCodes, setAllCodes] = useState([]);
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [highlightIdx, setHighlightIdx] = useState(-1);
  const suggestionsRef = useRef(null);

  // Manual accessory entry state
  const [manualDesc, setManualDesc] = useState('');
  const [manualQty, setManualQty] = useState('1');
  const [manualMrp, setManualMrp] = useState('');
  const [manualUnit, setManualUnit] = useState('NOS');

  // New product creation state
  const [newProd, setNewProd] = useState({ code: '', description: '', unit: 'NOS', product: 'SWITCH', mrp: '', brand: '', type: '', color: '', catalog_number: '', switch_subtype: '' });
  const [distinctValues, setDistinctValues] = useState({ codes: [], descriptions: [], brands: [], types: [], switch_subtypes: [], colors: [] });

  useEffect(() => {
    const fetchDistinct = async () => {
      try {
        const { data } = await axios.get(`${BACKEND_URL}/api/products/distinct-values`);
        setDistinctValues(data);
      } catch (e) { console.error(e); }
    };
    fetchDistinct();
  }, []);

  // Post-save dialog
  const [showPostSave, setShowPostSave] = useState(false);
  const [savedQuotationId, setSavedQuotationId] = useState(null);

  const [voucherNumber, setVoucherNumber] = useState('');
  const [buyerName, setBuyerName] = useState('');
  const [buyerMobile, setBuyerMobile] = useState('');
  const [buyerAddress, setBuyerAddress] = useState('');

  const [items, setItems] = useState([]);
  const [grandTotal, setGrandTotal] = useState(0);
  const [editingQuotationId, setEditingQuotationId] = useState(null);
  const [showSelections, setShowSelections] = useState(true);

  // Search state
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [showSearchResults, setShowSearchResults] = useState(false);

  useEffect(() => { fetchNextVoucher(); fetchAllCodes(); restoreDraft(); }, []);
  useEffect(() => { setGrandTotal(Math.ceil(items.reduce((s, i) => s + i.amount, 0))); }, [items]);

  // Auto-save draft on changes (debounced)
  useEffect(() => {
    if (items.length === 0 && !buyerName && !buyerMobile) return;
    const timer = setTimeout(() => {
      const draft = { voucherNumber, buyerName, buyerMobile, buyerAddress, items, areas, activeAreaId, editingQuotationId, savedAt: new Date().toISOString() };
      localStorage.setItem('ease_quote_draft', JSON.stringify(draft));
    }, 1000);
    return () => clearTimeout(timer);
  }, [items, buyerName, buyerMobile, buyerAddress, areas, voucherNumber]);

  // Save draft on tab close / navigation
  useEffect(() => {
    const saveDraft = () => {
      if (items.length === 0 && !buyerName && !buyerMobile) return;
      const draft = { voucherNumber, buyerName, buyerMobile, buyerAddress, items, areas, activeAreaId, editingQuotationId, savedAt: new Date().toISOString() };
      localStorage.setItem('ease_quote_draft', JSON.stringify(draft));
    };
    window.addEventListener('beforeunload', saveDraft);
    return () => window.removeEventListener('beforeunload', saveDraft);
  }, [items, buyerName, buyerMobile, buyerAddress, areas, voucherNumber, editingQuotationId]);

  const restoreDraft = () => {
    try {
      const saved = localStorage.getItem('ease_quote_draft');
      if (!saved) return;
      const draft = JSON.parse(saved);
      if (draft.items && draft.items.length > 0) {
        setVoucherNumber(draft.voucherNumber || '');
        setBuyerName(draft.buyerName || '');
        setBuyerMobile(draft.buyerMobile || '');
        setBuyerAddress(draft.buyerAddress || '');
        setItems(draft.items);
        if (draft.areas && draft.areas.length > 0) {
          setAreas(draft.areas);
          setActiveAreaId(draft.activeAreaId || draft.areas[0].id);
          setNextAreaId(Math.max(...draft.areas.map(a => a.id)) + 1);
        }
        if (draft.editingQuotationId) setEditingQuotationId(draft.editingQuotationId);
        toast.info('Draft restored — unsaved quotation loaded');
      }
    } catch (e) { /* ignore */ }
  };

  const clearDraft = () => {
    localStorage.removeItem('ease_quote_draft');
  };

  const fetchAllCodes = async () => {
    try {
      const { data } = await axios.get(`${BACKEND_URL}/api/products/codes-list`);
      setAllCodes(data);
    } catch (e) { console.error(e); }
  };

  const handleCodeChange = (val) => {
    const upper = val.toUpperCase();
    setCodeInput(upper);
    if (upper.length > 0) {
      const startsWith = allCodes.filter(c => c.code.startsWith(upper));
      const contains = allCodes.filter(c => !c.code.startsWith(upper) && c.code.includes(upper));
      const filtered = [...startsWith, ...contains];
      setSuggestions(filtered);
      setShowSuggestions(filtered.length > 0);
      setHighlightIdx(-1);
    } else {
      setSuggestions([]);
      setShowSuggestions(false);
    }
  };

  const selectSuggestion = (code) => {
    setCodeInput(code);
    setShowSuggestions(false);
    setSuggestions([]);
    setHighlightIdx(-1);
  };

  useEffect(() => {
    if (location.state?.editQuotation) {
      const q = location.state.editQuotation;
      loadQuotation(q);
      window.history.replaceState({}, document.title);
    }
  }, [location.state]);

  const fetchNextVoucher = async () => {
    try {
      const { data } = await axios.get(`${BACKEND_URL}/api/quotations/next-voucher/number`);
      setVoucherNumber(data.voucher_number);
    } catch (e) { console.error(e); }
  };

  const handleAddItem = async () => {
    if (!codeInput.trim()) return;
    try {
      const { data } = await axios.post(`${BACKEND_URL}/api/products/smart-lookup`, {
        code: codeInput.trim(),
        selections: buildSmartLookupSelections(categoryConfigs, sel),
      });

      // If brand not selected for this category, add with just code & description (no brand/price)
      const cat = data.product || '';
      const brandMap = buildBrandMap(categoryConfigs, sel);
      const hasBrand = !(cat in brandMap) || !!brandMap[cat];

      const qty = parseInt(qtyInput) || 1;
      const disc = parseFloat(discountInput) || 0;
      const mrp = hasBrand ? data.mrp : 0;
      const amount = qty * mrp * (1 - disc / 100);

      const newItem = {
        sr_no: items.length + 1,
        code: data.code,
        description: data.description,
        brand: hasBrand ? data.brand : '',
        product_type: hasBrand ? data.type : '',
        color: hasBrand ? data.color : '',
        catalog_number: hasBrand ? (data.catalog_number || '') : '',
        switch_subtype: hasBrand ? (data.switch_subtype || '') : '',
        quantity: qty,
        mrp: mrp,
        discount: disc,
        amount: parseFloat(amount.toFixed(2)),
        product: data.product || '',
        unit: data.unit || 'NOS',
        area: activeArea.name,
      };
      setItems([...items, newItem]);
      setCodeInput('');
      setSuggestions([]);
      setShowSuggestions(false);
      setQtyInput('1');
      if (codeInputRef.current) codeInputRef.current.focus();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Product not found');
    }
  };

  const handleInsertItem = async (afterIndex) => {
    if (!insertCode.trim()) return;
    try {
      const { data } = await axios.post(`${BACKEND_URL}/api/products/smart-lookup`, {
        code: insertCode.trim(),
        selections: buildSmartLookupSelections(categoryConfigs, sel),
      });
      const cat = data.product || '';
      const brandMap = buildBrandMap(categoryConfigs, sel);
      const hasBrand = !(cat in brandMap) || !!brandMap[cat];
      const qty = parseInt(insertQty) || 1;
      const disc = parseFloat(insertDisc) || 0;
      const mrp = hasBrand ? data.mrp : 0;
      const amount = qty * mrp * (1 - disc / 100);
      const newItem = {
        sr_no: 0,
        code: data.code,
        description: data.description,
        brand: hasBrand ? data.brand : '',
        product_type: hasBrand ? data.type : '',
        color: hasBrand ? data.color : '',
        catalog_number: hasBrand ? (data.catalog_number || '') : '',
        switch_subtype: hasBrand ? (data.switch_subtype || '') : '',
        quantity: qty, mrp, discount: disc,
        amount: parseFloat(amount.toFixed(2)),
        product: data.product || '',
        unit: data.unit || 'NOS',
        area: items[afterIndex]?.area || activeArea.name,
      };
      const updated = [...items];
      updated.splice(afterIndex + 1, 0, newItem);
      updated.forEach((item, i) => { item.sr_no = i + 1; });
      setItems(updated);
      setInsertAfterIdx(null);
      setInsertCode('');
      setInsertQty('1');
      setInsertDisc('0');
      toast.success(`Inserted ${data.code} at Sr. ${afterIndex + 2}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Product not found');
    }
  };

  const startEditItem = (itemIdx) => {
    setEditingItemIdx(itemIdx);
    setEditCode(items[itemIdx].code);
    setInsertAfterIdx(null); // close any insert form
  };

  const handleEditItem = async (itemIdx) => {
    if (!editCode.trim()) return;
    if (editCode.trim().toUpperCase() === items[itemIdx].code) { setEditingItemIdx(null); return; }
    try {
      const { data } = await axios.post(`${BACKEND_URL}/api/products/smart-lookup`, {
        code: editCode.trim(),
        selections: buildSmartLookupSelections(categoryConfigs, sel),
      });
      const cat = data.product || '';
      const brandMap = buildBrandMap(categoryConfigs, sel);
      const hasBrand = !(cat in brandMap) || !!brandMap[cat];
      const oldItem = items[itemIdx];
      const qty = oldItem.quantity;
      const disc = oldItem.discount;
      const mrp = hasBrand ? data.mrp : 0;
      const amount = qty * mrp * (1 - disc / 100);
      const updated = [...items];
      updated[itemIdx] = {
        ...oldItem,
        code: data.code,
        description: data.description,
        brand: hasBrand ? data.brand : '',
        product_type: hasBrand ? data.type : '',
        color: hasBrand ? data.color : '',
        catalog_number: hasBrand ? (data.catalog_number || '') : '',
        switch_subtype: hasBrand ? (data.switch_subtype || '') : '',
        mrp, amount: parseFloat(amount.toFixed(2)),
        product: data.product || '',
        unit: data.unit || 'NOS',
      };
      setItems(updated);
      setEditingItemIdx(null);
      setEditCode('');
      toast.success(`Sr. ${oldItem.sr_no}: Changed to ${data.code}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Product not found');
    }
  };

  const handleAddManualItem = () => {
    if (!manualDesc.trim()) { toast.error('Enter description'); return; }
    const mrp = parseFloat(manualMrp) || 0;
    const qty = parseInt(manualQty) || 1;
    const amount = qty * mrp;
    const newItem = {
      sr_no: items.length + 1,
      code: '-',
      description: manualDesc.trim().toUpperCase(),
      brand: '-',
      product_type: '-',
      color: '-',
      catalog_number: '',
      switch_subtype: '',
      quantity: qty,
      mrp: mrp,
      discount: 0,
      amount: parseFloat(amount.toFixed(2)),
      product: 'ACCESSORY',
      unit: manualUnit,
      area: activeArea.name,
    };
    setItems([...items, newItem]);
    setManualDesc('');
    setManualQty('1');
    setManualMrp('');
    setManualUnit('NOS');
  };

  const createProduct = async (addToQuotation = false) => {
    if (!newProd.code.trim() || !newProd.description.trim()) { toast.error('Code and Description are required'); return; }
    if (!newProd.brand.trim()) { toast.error('Brand is required'); return; }
    try {
      await axios.post(`${BACKEND_URL}/api/products`, {
        code: newProd.code.trim().toUpperCase(),
        description: newProd.description.trim().toUpperCase(),
        brand: newProd.brand.trim().toUpperCase(),
        type: newProd.type.trim().toUpperCase() || '-',
        color: newProd.color.trim().toUpperCase() || '-',
        mrp: parseFloat(newProd.mrp) || 0,
        product: newProd.product,
        unit: newProd.unit,
        catalog_number: newProd.catalog_number.trim(),
        switch_subtype: newProd.switch_subtype.trim().toUpperCase(),
      });

      if (addToQuotation) {
        const mrp = parseFloat(newProd.mrp) || 0;
        const newItem = {
          sr_no: items.length + 1,
          code: newProd.code.trim().toUpperCase(),
          description: newProd.description.trim().toUpperCase(),
          brand: newProd.brand.trim().toUpperCase(),
          product_type: newProd.type.trim().toUpperCase() || '-',
          color: newProd.color.trim().toUpperCase() || '-',
          catalog_number: newProd.catalog_number.trim(),
          switch_subtype: newProd.switch_subtype.trim().toUpperCase(),
          quantity: 1,
          mrp: mrp,
          discount: 0,
          amount: mrp,
          product: newProd.product,
          unit: newProd.unit,
          area: activeArea.name,
        };
        setItems(prev => [...prev, newItem]);
        toast.success(`Product ${newProd.code.toUpperCase()} created & added to quotation`);
      } else {
        toast.success(`Product ${newProd.code.toUpperCase()} created`);
      }

      setNewProd({ code: '', description: '', unit: 'NOS', product: 'SWITCH', mrp: '', brand: '', type: '', color: '', catalog_number: '', switch_subtype: '' });
      fetchAllCodes();
      // Refresh distinct values for autocomplete
      try {
        const { data: dv } = await axios.get(`${BACKEND_URL}/api/products/distinct-values`);
        setDistinctValues(dv);
      } catch (e2) { /* ignore */ }
    } catch (e) { toast.error(e.response?.data?.detail || 'Error creating product'); }
  };

  const handleCodeKeyDown = (e) => {
    if (showSuggestions && suggestions.length > 0) {
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setHighlightIdx(prev => {
          const next = prev < suggestions.length - 1 ? prev + 1 : 0;
          scrollToHighlighted(next);
          return next;
        });
        return;
      }
      if (e.key === 'ArrowUp') {
        e.preventDefault();
        setHighlightIdx(prev => {
          const next = prev > 0 ? prev - 1 : suggestions.length - 1;
          scrollToHighlighted(next);
          return next;
        });
        return;
      }
      if (e.key === 'Enter' && highlightIdx >= 0) {
        e.preventDefault();
        selectSuggestion(suggestions[highlightIdx].code);
        return;
      }
      if (e.key === 'Escape') {
        setShowSuggestions(false);
        return;
      }
    }
    if (e.key === 'Enter') { e.preventDefault(); handleAddItem(); }
  };

  const scrollToHighlighted = (idx) => {
    if (suggestionsRef.current) {
      const items = suggestionsRef.current.children;
      if (items[idx]) items[idx].scrollIntoView({ block: 'nearest' });
    }
  };

  const updateItemField = (index, field, value) => {
    const updated = [...items];
    updated[index][field] = parseFloat(value) || 0;
    const qty = updated[index].quantity;
    const mrp = updated[index].mrp;
    const disc = updated[index].discount;
    updated[index].amount = parseFloat((qty * mrp * (1 - disc / 100)).toFixed(2));
    updated.forEach((item, i) => { item.sr_no = i + 1; });
    setItems(updated);
  };

  const removeItem = (index) => {
    const updated = items.filter((_, i) => i !== index);
    updated.forEach((item, i) => { item.sr_no = i + 1; });
    setItems(updated);
  };

  const [isUpdatingPrices, setIsUpdatingPrices] = useState(false);

  const updateAllPrices = async () => {
    const areaItems = items.filter(it => it.area === activeArea.name);
    if (areaItems.length === 0) return;
    setIsUpdatingPrices(true);
    const lookupPayload = { selections: buildSmartLookupSelections(categoryConfigs, sel) };
    let updatedCount = 0;
    const updated = [...items];
    for (let i = 0; i < updated.length; i++) {
      if (updated[i].area !== activeArea.name) continue; // Only update active area
      if (updated[i].product === 'ACCESSORY') continue;
      try {
        const { data } = await axios.post(`${BACKEND_URL}/api/products/smart-lookup`, {
          code: updated[i].code, ...lookupPayload
        });
        const oldMrp = updated[i].mrp;
        updated[i].description = data.description;
        updated[i].brand = data.brand;
        updated[i].product_type = data.type;
        updated[i].color = data.color;
        updated[i].mrp = data.mrp;
        updated[i].product = data.product || '';
        updated[i].catalog_number = data.catalog_number || '';
        updated[i].switch_subtype = data.switch_subtype || '';
        updated[i].amount = parseFloat((updated[i].quantity * data.mrp * (1 - updated[i].discount / 100)).toFixed(2));
        if (oldMrp !== data.mrp) updatedCount++;
      } catch (e) {
        console.error(`Failed to update ${updated[i].code}:`, e);
      }
    }
    setItems(updated);
    setIsUpdatingPrices(false);
    if (updatedCount > 0) {
      toast.success(`${updatedCount} item${updatedCount > 1 ? 's' : ''} updated with new prices`);
    } else {
      toast.info('All items already match current selections');
    }
  };

  // Auto-update quotation items when dropdown selections change (SCOPED TO ACTIVE AREA)
  const activeSelectionsKey = JSON.stringify({ areaId: activeAreaId, sel: activeArea.selections });
  useEffect(() => {
    const areaItems = items.filter(it => it.area === activeArea.name);
    if (areaItems.length === 0) return;
    const refreshItems = async () => {
      const lookupPayload = { selections: buildSmartLookupSelections(categoryConfigs, sel) };
      const updated = [...items];
      let changed = false;
      for (let i = 0; i < updated.length; i++) {
        if (updated[i].area !== activeArea.name) continue; // Only update active area
        if (updated[i].product === 'ACCESSORY') continue;
        try {
          const { data } = await axios.post(`${BACKEND_URL}/api/products/smart-lookup`, {
            code: updated[i].code, ...lookupPayload
          });
          if (updated[i].brand !== data.brand || updated[i].color !== data.color || updated[i].product_type !== data.type || updated[i].mrp !== data.mrp || updated[i].catalog_number !== (data.catalog_number || '') || updated[i].switch_subtype !== (data.switch_subtype || '')) {
            updated[i].description = data.description;
            updated[i].brand = data.brand;
            updated[i].product_type = data.type;
            updated[i].color = data.color;
            updated[i].mrp = data.mrp;
            updated[i].product = data.product || '';
            updated[i].catalog_number = data.catalog_number || '';
            updated[i].switch_subtype = data.switch_subtype || '';
            updated[i].amount = parseFloat((updated[i].quantity * data.mrp * (1 - updated[i].discount / 100)).toFixed(2));
            changed = true;
          }
        } catch (e) { /* ignore lookup errors */ }
      }
      if (changed) setItems(updated);
    };
    refreshItems();
  }, [activeSelectionsKey]);

  const saveQuotation = async () => {
    if (items.length === 0) { toast.error('Add items first'); return; }
    try {
      const payload = { voucher_number: voucherNumber, buyer_name: buyerName, buyer_mobile: buyerMobile, buyer_address: buyerAddress, items, grand_total: grandTotal, areas: areas.map(a => ({ name: a.name, selections: a.selections })) };
      let qId;
      if (editingQuotationId) {
        await axios.put(`${BACKEND_URL}/api/quotations/${editingQuotationId}`, payload);
        qId = editingQuotationId;
        toast.success('Quotation updated');
      } else {
        const { data } = await axios.post(`${BACKEND_URL}/api/quotations`, payload);
        qId = data.id;
        setEditingQuotationId(qId);
        toast.success('Quotation saved');
      }
      setSavedQuotationId(qId);
      setShowPostSave(true);
      clearDraft();
    } catch (e) { toast.error('Error saving quotation'); }
  };

  const resetForm = () => {
    setItems([]); setBuyerName(''); setBuyerMobile(''); setBuyerAddress(''); setEditingQuotationId(null); fetchNextVoucher();
    setAreas([{ id: 1, name: 'GENERAL', selections: JSON.parse(JSON.stringify(defaultSelections)) }]);
    setActiveAreaId(1); setNextAreaId(2);
    clearDraft();
  };

  const duplicateQuotation = async () => {
    if (items.length === 0) { toast.error('Nothing to duplicate'); return; }
    try {
      const { data } = await axios.get(`${BACKEND_URL}/api/quotations/next-voucher/number`);
      setVoucherNumber(data.voucher_number);
      setEditingQuotationId(null);
      toast.success(`Duplicated as ${data.voucher_number} — Save to create new quotation`);
    } catch (e) {
      toast.error('Error generating new voucher number');
    }
  };

  const handleSearch = async (query) => {
    setSearchQuery(query);
    if (query.trim().length < 2) { setSearchResults([]); setShowSearchResults(false); return; }
    try {
      const { data } = await axios.get(`${BACKEND_URL}/api/quotations/search?q=${encodeURIComponent(query.trim())}`);
      setSearchResults(data);
      setShowSearchResults(true);
    } catch (e) { console.error(e); }
  };

  // Infer dropdown selections from items for a given area
  const inferSelectionsFromItems = (areaItems) => {
    const inferred = buildDefaultSelections(categoryConfigs);
    const catMap = buildCatMap(categoryConfigs);
    areaItems.forEach(item => {
      const catId = catMap[item.product];
      if (!catId || !inferred[catId]) return;
      if (item.brand && !inferred[catId].brand) inferred[catId].brand = item.brand;
      if (item.product_type && 'type' in inferred[catId] && !inferred[catId].type) inferred[catId].type = item.product_type;
      if (item.color && 'color' in inferred[catId] && !inferred[catId].color) inferred[catId].color = item.color;
      if (item.switch_subtype && 'subtype' in inferred[catId] && !inferred[catId].subtype) inferred[catId].subtype = item.switch_subtype;
    });
    return inferred;
  };

  const loadQuotation = (q) => {
    setVoucherNumber(q.voucher_number);
    setBuyerName(q.buyer_name || '');
    setBuyerMobile(q.buyer_mobile || '');
    setBuyerAddress(q.buyer_address || '');
    setItems(q.items || []);
    setEditingQuotationId(q.id);
    setShowSearchResults(false);
    setSearchQuery('');
    // Restore areas if saved, otherwise create from item areas
    if (q.areas && q.areas.length > 0) {
      const restored = q.areas.map((a, i) => {
        let sel = a.selections || JSON.parse(JSON.stringify(defaultSelections));
        // If selections are all empty, try inferring from items
        const hasAnySelection = Object.values(sel).some(catSel => typeof catSel === 'object' && Object.values(catSel).some(v => v));
        if (!hasAnySelection) {
          const areaItems = (q.items || []).filter(it => (it.area || 'GENERAL') === a.name);
          sel = inferSelectionsFromItems(areaItems);
        }
        return { id: i + 1, name: a.name, selections: sel };
      });
      setAreas(restored);
      setActiveAreaId(restored[0].id);
      setNextAreaId(restored.length + 1);
    } else {
      // Legacy quotations without areas: derive areas and selections from items
      const areaNames = [...new Set((q.items || []).map(it => it.area || 'GENERAL'))];
      const restored = areaNames.map((name, i) => {
        const areaItems = (q.items || []).filter(it => (it.area || 'GENERAL') === name);
        return { id: i + 1, name, selections: inferSelectionsFromItems(areaItems) };
      });
      if (restored.length === 0) restored.push({ id: 1, name: 'GENERAL', selections: JSON.parse(JSON.stringify(defaultSelections)) });
      setAreas(restored);
      setActiveAreaId(restored[0].id);
      setNextAreaId(restored.length + 1);
      // Tag legacy items with GENERAL
      if (q.items?.some(it => !it.area)) {
        setItems(prev => prev.map(it => it.area ? it : { ...it, area: 'GENERAL' }));
      }
    }
    toast.success(`Loaded ${q.voucher_number}`);
  };

  const shareWhatsApp = async () => {
    if (!editingQuotationId) {
      toast.error('Save the quotation first to share via WhatsApp');
      return;
    }
    try {
      const response = await axios.get(`${BACKEND_URL}/api/quotations/${editingQuotationId}/pdf`, { responseType: 'blob' });
      const pdfBlob = new Blob([response.data], { type: 'application/pdf' });
      const pdfFile = new File([pdfBlob], `Quotation_${voucherNumber}.pdf`, { type: 'application/pdf' });

      // Try Web Share API (works on mobile)
      let shared = false;
      try {
        if (navigator.canShare && navigator.canShare({ files: [pdfFile] })) {
          await navigator.share({
            files: [pdfFile],
            title: `Quotation ${voucherNumber} - Veer Electrical`,
            text: `Quotation ${voucherNumber} from Veer Electrical (Loharchawl), Mumbai`,
          });
          shared = true;
          toast.success('Shared successfully');
        }
      } catch (shareErr) {
        if (shareErr.name !== 'AbortError') console.error('Share failed:', shareErr);
      }

      if (!shared) {
        // Fallback: download PDF via direct URL (iframe-safe), then open WhatsApp
        window.location.href = `${BACKEND_URL}/api/quotations/${editingQuotationId}/pdf?token=${localStorage.getItem('access_token')}`;

        const phone = buyerMobile ? buyerMobile.replace(/\D/g, '') : '';
        const fullPhone = phone.length === 10 ? `91${phone}` : phone;
        const msg = encodeURIComponent(`Quotation ${voucherNumber} from Veer Electrical (Loharchawl), Mumbai.\nTotal: Rs.${grandTotal.toFixed(2)}\nPlease find the PDF attached.`);
        const waUrl = fullPhone ? `https://wa.me/${fullPhone}?text=${msg}` : `https://wa.me/?text=${msg}`;
        setTimeout(() => { window.location.href = waUrl; }, 1500);
        toast.info('PDF downloading! WhatsApp will open shortly - attach the PDF there.');
      }
    } catch (e) {
      toast.error('Error generating PDF');
      console.error(e);
    }
  };

  const handlePrint = () => {
    // If quotation is saved, use the backend PDF endpoint (most reliable)
    if (editingQuotationId) {
      const token = localStorage.getItem('access_token');
      window.location.href = `${BACKEND_URL}/api/quotations/${editingQuotationId}/pdf?token=${token}`;
      toast.info('PDF downloading — open it to print');
      return;
    }

    // For unsaved quotations, build print HTML in a hidden iframe
    // Group items by area for printing
    const areaOrder = [];
    const areaGroups = {};
    items.forEach(item => {
      const a = item.area || 'GENERAL';
      if (!areaGroups[a]) { areaGroups[a] = []; areaOrder.push(a); }
      areaGroups[a].push(item);
    });
    let itemRows = '';
    areaOrder.forEach(areaName => {
      if (areaOrder.length > 1 || areaName !== 'GENERAL') {
        itemRows += `<tr class="area-hdr"><td colspan="10" style="background:#E8EDFB;font-weight:bold;font-size:7px;padding:2px 4px;color:#0066FF;text-transform:uppercase;letter-spacing:1px;">${areaName}</td></tr>`;
      }
      areaGroups[areaName].forEach(i => {
        itemRows += `<tr><td>${i.sr_no}</td><td class="mono">${i.catalog_number || ''}</td><td>${i.brand}</td><td>${i.product_type}</td><td>${i.switch_subtype || ''}</td><td>${i.color || ''}</td><td>${i.description}</td><td class="c">${i.quantity}</td><td>${i.unit || 'NOS'}</td><td class="r">${i.amount.toFixed(2)}</td></tr>`;
      });
    });
    const emptyCount = Math.max(0, 20 - items.length - (areaOrder.length > 1 ? areaOrder.length : 0));
    const emptyRows = Array(emptyCount).fill('<tr><td></td><td></td><td></td><td></td><td></td><td></td><td></td><td></td><td></td><td></td></tr>').join('');

    const printHTML = buildQuotationHTML(itemRows, emptyRows);
    printViaIframe(printHTML);
  };

  const handleConsolidatedPrint = () => {
    if (items.length === 0) { toast.error('No items to consolidate'); return; }

    // If saved, use backend endpoint
    if (editingQuotationId) {
      const token = localStorage.getItem('access_token');
      window.location.href = `${BACKEND_URL}/api/quotations/${editingQuotationId}/consolidated-pdf?token=${token}`;
      toast.info('Consolidated Material List PDF downloading');
      return;
    }

    // Consolidate: group by code+brand+type+color+switchSubtype+unit, sum quantities
    const consolidated = {};
    items.forEach(item => {
      const key = `${item.code}|${item.brand}|${item.product_type}|${item.color}|${item.switch_subtype || ''}|${item.unit || 'NOS'}|${item.catalog_number || ''}`;
      if (!consolidated[key]) {
        consolidated[key] = { ...item, quantity: 0, amount: 0 };
      }
      consolidated[key].quantity += parseInt(item.quantity) || 0;
      consolidated[key].amount += item.amount;
    });
    const cItems = Object.values(consolidated);
    const totalQty = cItems.reduce((s, i) => s + i.quantity, 0);

    let sr = 0;
    const cRows = cItems.map(i => {
      sr++;
      return `<tr><td>${sr}</td><td class="mono">${i.catalog_number || ''}</td><td>${i.brand}</td><td>${i.product_type}</td><td>${i.switch_subtype || ''}</td><td>${i.color || ''}</td><td>${i.description}</td><td class="c">${i.quantity}</td><td>${i.unit || 'NOS'}</td><td class="r">${i.amount.toFixed(2)}</td></tr>`;
    }).join('');
    const emptyCount = Math.max(0, 20 - cItems.length);
    const emptyRows = Array(emptyCount).fill('<tr><td></td><td></td><td></td><td></td><td></td><td></td><td></td><td></td><td></td><td></td></tr>').join('');

    const printHTML = buildMaterialListHTML(cRows, emptyRows, totalQty);
    printViaIframe(printHTML);
  };

  const printViaIframe = (html) => {
    const printFrame = document.createElement('iframe');
    printFrame.style.position = 'fixed';
    printFrame.style.right = '0';
    printFrame.style.bottom = '0';
    printFrame.style.width = '0';
    printFrame.style.height = '0';
    printFrame.style.border = 'none';
    document.body.appendChild(printFrame);
    printFrame.contentDocument.write(html);
    printFrame.contentDocument.close();
    printFrame.contentWindow.focus();
    printFrame.contentWindow.print();
    setTimeout(() => document.body.removeChild(printFrame), 5000);
  };

  const commonStyles = `
    @page { size: 148mm 210mm; margin: 5mm; }
    * { box-sizing: border-box; margin: 0; padding: 0; }
    body { font-family: Arial, Helvetica, sans-serif; width: 138mm; height: 200mm; display: flex; flex-direction: column; color: #222; }
    .hdr { display: flex; height: 50px; flex-shrink: 0; }
    .hdr-l { background: #1a1a2e; flex: 0 0 55%; display: flex; flex-direction: column; justify-content: center; padding-left: 14px; clip-path: polygon(0 0, 93% 0, 100% 100%, 0 100%); }
    .hdr-l h1 { color: #fff; font-size: 20px; letter-spacing: 2.5px; margin: 0; }
    .hdr-l .loc { color: #bbb; font-size: 9px; margin-top: 2px; }
    .hdr-r { flex: 1; display: flex; align-items: center; justify-content: flex-end; padding-right: 14px; }
    .hdr-r span { color: #fff; font-size: 18px; font-weight: bold; letter-spacing: 3px; }
    .info { display: flex; justify-content: space-between; padding: 8px 14px 6px; flex-shrink: 0; }
    .info-l { font-size: 11px; line-height: 1.6; }
    .info-r { text-align: right; font-size: 11px; line-height: 1.6; }
    .info-r .lbl { font-size: 9px; color: #999; font-weight: bold; text-transform: uppercase; letter-spacing: 1px; }
    .info-r .name { font-size: 13px; font-weight: bold; color: #c0392b; }
    .info-r .addr { font-size: 10px; color: #555; }
    .tbl { padding: 0 14px; flex: 1; display: flex; flex-direction: column; }
    table { width: 100%; border-collapse: collapse; flex: 1; border: 1.5px solid #333; }
    thead th { color: #fff; font-size: 8px; padding: 3px 4px; text-align: left; text-transform: uppercase; }
    thead th.r { text-align: right; }
    thead th.c { text-align: center; }
    tbody td { padding: 0 4px; font-size: 8px; border-bottom: 0.3px solid #ddd; border-left: 0.5px solid #eee; height: 9.5px; line-height: 9.5px; overflow: hidden; white-space: nowrap; }
    tbody tr:nth-child(even) td { background: #f9f9f9; }
    td.r { text-align: right; }
    td.c { text-align: center; }
    td.mono { font-family: monospace; font-weight: 600; }
    .totals { padding: 5px 14px 0; display: flex; justify-content: flex-end; flex-shrink: 0; }
    .totals-box { width: 42%; }
    .tot-row { display: flex; justify-content: space-between; padding: 3px 0; font-size: 10px; border-bottom: 0.5px solid #ddd; }
    .tot-row.grand { border-top: 1.5px solid #1a1a2e; border-bottom: 1.5px solid #1a1a2e; font-weight: bold; font-size: 13px; color: #c0392b; margin-top: 2px; padding: 5px 0; }
    .foot { padding: 8px 14px 10px; flex-shrink: 0; display: flex; justify-content: space-between; align-items: flex-end; border-top: 0.5px solid #ddd; margin-top: 5px; }
    .terms { max-width: 60%; }
    .terms b { font-size: 10px; text-transform: uppercase; display: block; margin-bottom: 3px; }
    .terms p { font-size: 8px; color: #333; line-height: 1.6; }
    .sign { text-align: right; }
    .sign .co { font-size: 11px; font-weight: bold; color: #c0392b; margin-bottom: 18px; }
    .sign .line { border-top: 0.5px solid #222; width: 100px; margin-left: auto; margin-bottom: 3px; }
    .sign .lbl { font-size: 9px; font-weight: bold; }
  `;

  const buildQuotationHTML = (itemRows, emptyRows) => {
    return `<html><head><title>Quotation - ${voucherNumber}</title>
    <style>${commonStyles}
    .hdr-r { background: #c0392b; }
    thead th { background: #c0392b; }
    </style></head><body>
    <div class="hdr">
      <div class="hdr-l"><h1>VEER ELECTRICAL</h1><div class="loc">(Loharchawl), Mumbai</div></div>
      <div class="hdr-r"><span>QUOTATION</span></div>
    </div>
    <div class="info">
      <div class="info-l">
        <div><b>Quotation No:</b> ${voucherNumber}</div>
        <div><b>Date:</b> ${new Date().toLocaleDateString('en-IN', {day:'2-digit',month:'short',year:'numeric'})}</div>
      </div>
      <div class="info-r">
        <div class="lbl">Quotation To</div>
        <div class="name">${buyerName || '-'}</div>
        ${buyerMobile ? `<div>Mobile: ${buyerMobile}</div>` : ''}
        ${buyerAddress ? `<div class="addr">${buyerAddress}</div>` : ''}
      </div>
    </div>
    <div class="tbl">
      <table>
        <thead><tr><th>Sr.</th><th>Catalog No.</th><th>Brand</th><th>Sub Brand</th><th>Type</th><th>Color</th><th>Description</th><th class="c">Qty</th><th>Unit</th><th class="r">Amount</th></tr></thead>
        <tbody>${itemRows}${emptyRows}</tbody>
      </table>
    </div>
    <div class="totals">
      <div class="totals-box">
        <div class="tot-row"><span>Sub Total</span><span>${items.reduce((s,i) => s+i.amount, 0).toFixed(2)}</span></div>
        <div class="tot-row"><span>GST (18%)</span><span>As Applicable</span></div>
        <div class="tot-row grand"><span>Grand Total</span><span>${grandTotal.toFixed(2)}</span></div>
      </div>
    </div>
    <div class="foot">
      <div class="terms">
        <b>Terms & Conditions</b>
        <p>1. 100% Advance Payment. 2. No returns/exchange for colour other than white. 3. No returns of material in bad condition. 4. Warranty claimed by company, not distributor. 5. Only 10% returns for white range. 6. Delivery charges extra. 7. 18% GST applicable.</p>
      </div>
      <div class="sign">
        <div class="co">Veer Electrical</div>
        <div class="line"></div>
        <div class="lbl">Authorised Sign</div>
      </div>
    </div>
    </body></html>`;
  };

  const buildMaterialListHTML = (cRows, emptyRows, totalQty) => {
    return `<html><head><title>Material List - ${voucherNumber}</title>
    <style>${commonStyles}
    .hdr-r { background: #059669; }
    thead th { background: #059669; }
    .info-r .name { color: #059669; }
    .tot-row.grand { color: #059669; }
    .sign .co { color: #059669; }
    </style></head><body>
    <div class="hdr">
      <div class="hdr-l"><h1>VEER ELECTRICAL</h1><div class="loc">(Loharchawl), Mumbai</div></div>
      <div class="hdr-r"><span>MATERIAL LIST</span></div>
    </div>
    <div class="info">
      <div class="info-l">
        <div><b>Ref. Quotation:</b> ${voucherNumber}</div>
        <div><b>Date:</b> ${new Date().toLocaleDateString('en-IN', {day:'2-digit',month:'short',year:'numeric'})}</div>
        <div><b>Total Items:</b> ${totalQty} units (consolidated)</div>
      </div>
      <div class="info-r">
        <div class="lbl">Prepared For</div>
        <div class="name">${buyerName || '-'}</div>
        ${buyerMobile ? `<div>Mobile: ${buyerMobile}</div>` : ''}
      </div>
    </div>
    <div class="tbl">
      <table>
        <thead><tr><th>Sr.</th><th>Catalog No.</th><th>Brand</th><th>Sub Brand</th><th>Type</th><th>Color</th><th>Description</th><th class="c">Qty</th><th>Unit</th><th class="r">Amount</th></tr></thead>
        <tbody>${cRows}${emptyRows}</tbody>
      </table>
    </div>
    <div class="totals">
      <div class="totals-box">
        <div class="tot-row"><span>Total Units</span><span>${totalQty}</span></div>
        <div class="tot-row grand"><span>Grand Total</span><span>${grandTotal.toFixed(2)}</span></div>
      </div>
    </div>
    <div class="foot">
      <div class="sign">
        <div class="co">Veer Electrical</div>
        <div class="line"></div>
        <div class="lbl">Authorised Sign</div>
      </div>
    </div>
    </body></html>`;
  };

  // Field configs for each dropdown group
  // Category management functions
  const addCategory = () => {
    if (!newCatName.trim() || !newCatProductKey.trim()) { toast.error('Name and Product Key are required'); return; }
    const id = generateCategoryId(newCatName);
    const newCat = { id, name: newCatName.trim().toUpperCase(), productKey: newCatProductKey.trim().toUpperCase(), color: getNextColor(categoryConfigs), fields: [...newCatFields] };
    setCategoryConfigs(prev => [...prev, newCat]);
    // Add empty selection for new category in all areas
    setAreas(prev => prev.map(a => ({ ...a, selections: { ...a.selections, [id]: buildEmptySelection(newCat) } })));
    setShowAddCategory(false);
    setNewCatName('');
    setNewCatProductKey('');
    setNewCatFields(['brand']);
    toast.success(`Category "${newCat.name}" added`);
  };

  const removeCategory = (catId) => {
    if (categoryConfigs.length <= 1) { toast.error('Cannot remove the last category'); return; }
    const cat = categoryConfigs.find(c => c.id === catId);
    setCategoryConfigs(prev => prev.filter(c => c.id !== catId));
    setAreas(prev => prev.map(a => { const s = { ...a.selections }; delete s[catId]; return { ...a, selections: s }; }));
    setConfirmDeleteCatId(null);
    toast.success(`Category "${cat?.name}" removed`);
  };

  const moveCategoryUp = (catId) => {
    setCategoryConfigs(prev => {
      const idx = prev.findIndex(c => c.id === catId);
      if (idx <= 0) return prev;
      const arr = [...prev];
      [arr[idx - 1], arr[idx]] = [arr[idx], arr[idx - 1]];
      return arr;
    });
  };

  const moveCategoryDown = (catId) => {
    setCategoryConfigs(prev => {
      const idx = prev.findIndex(c => c.id === catId);
      if (idx < 0 || idx >= prev.length - 1) return prev;
      const arr = [...prev];
      [arr[idx], arr[idx + 1]] = [arr[idx + 1], arr[idx]];
      return arr;
    });
  };

  const renameCategoryConfirm = (catId) => {
    if (!editingCatName.trim()) { setEditingCatId(null); return; }
    setCategoryConfigs(prev => prev.map(c => c.id === catId ? { ...c, name: editingCatName.trim().toUpperCase() } : c));
    setEditingCatId(null);
  };

  const toggleCategoryField = (catId, field) => {
    setCategoryConfigs(prev => prev.map(c => {
      if (c.id !== catId) return c;
      const has = c.fields.includes(field);
      if (field === 'brand') return c; // brand is always required
      const newFields = has ? c.fields.filter(f => f !== field) : [...c.fields, field];
      return { ...c, fields: newFields };
    }));
    // Update area selections to match new fields
    setAreas(prev => prev.map(a => {
      const cat = categoryConfigs.find(c => c.id === catId);
      if (!cat) return a;
      const updatedCat = { ...cat, fields: cat.fields.includes(field) ? cat.fields.filter(f => f !== field) : [...cat.fields, field] };
      const newSel = buildEmptySelection(updatedCat);
      const oldSel = a.selections[catId] || {};
      // Preserve existing values for kept fields
      updatedCat.fields.forEach(f => { if (oldSel[f]) newSel[f] = oldSel[f]; });
      return { ...a, selections: { ...a.selections, [catId]: newSel } };
    }));
  };

  return (
    <div className="min-h-screen bg-[#F8F9FB]">
      <header className="bg-white border-b border-border shadow-sm">
        <div className="px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Button variant="outline" size="sm" onClick={() => navigate('/dashboard')} data-testid="back-to-main">
              <ArrowLeft size={14} className="mr-1" /> Dashboard
            </Button>
            <h1 className="text-lg sm:text-xl font-semibold tracking-tight" data-testid="dashboard-title">Create Quotation</h1>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-xs text-muted-foreground hidden sm:block" data-testid="user-email">{user?.email}</span>
            <Button variant="outline" size="sm" onClick={() => navigate('/products')} data-testid="products-nav-button">
              <Package size={14} className="mr-1" /> Products
            </Button>
            <Button variant="outline" size="sm" onClick={() => navigate('/quotations')} data-testid="quotations-nav-button">
              <Receipt size={14} className="mr-1" /> Non-Billed
            </Button>
            <Button variant="outline" size="sm" onClick={() => navigate('/catalog')} data-testid="catalog-nav-button">
              <MagnifyingGlass size={14} className="mr-1" /> Catalog
            </Button>
            <Button variant="outline" size="sm" onClick={logout} data-testid="logout-button">
              <SignOut size={14} className="mr-1" /> Logout
            </Button>
          </div>
        </div>
      </header>

      <div className="p-3 sm:p-4 space-y-3 max-w-[1400px] mx-auto">
        {/* Search Quotations */}
        <div className="relative">
          <div className="flex items-center gap-2 bg-white border border-border rounded-sm shadow-sm px-3 py-2">
            <MagnifyingGlass size={16} className="text-muted-foreground" />
            <input
              value={searchQuery}
              onChange={(e) => handleSearch(e.target.value)}
              onFocus={() => { if (searchResults.length > 0) setShowSearchResults(true); }}
              onBlur={() => setTimeout(() => setShowSearchResults(false), 200)}
              placeholder="Search by party name or quotation number..."
              className="flex-1 text-sm outline-none bg-transparent"
              data-testid="search-quotation-input"
            />
          </div>
          {showSearchResults && searchResults.length > 0 && (
            <div className="absolute z-50 left-0 right-0 mt-0.5 bg-white border border-border rounded-sm shadow-lg max-h-60 overflow-y-auto" data-testid="search-results">
              {searchResults.map((q, i) => (
                <div key={q.id} onMouseDown={() => loadQuotation(q)}
                  className="px-3 py-2 cursor-pointer hover:bg-[#F0F4FF] border-b border-border last:border-b-0 flex items-center justify-between"
                  data-testid={`search-result-${i}`}
                >
                  <div>
                    <span className="font-mono font-semibold text-xs text-[#0066FF]">{q.voucher_number}</span>
                    <span className="text-xs text-muted-foreground ml-2">{q.buyer_name || 'No name'}</span>
                    {q.buyer_mobile && <span className="text-xs text-muted-foreground ml-2">{q.buyer_mobile}</span>}
                  </div>
                  <span className="text-xs font-semibold">{q.grand_total?.toFixed(2)}</span>
                </div>
              ))}
            </div>
          )}
          {showSearchResults && searchResults.length === 0 && searchQuery.length >= 2 && (
            <div className="absolute z-50 left-0 right-0 mt-0.5 bg-white border border-border rounded-sm shadow-lg px-3 py-3 text-xs text-muted-foreground text-center">
              No quotations found
            </div>
          )}
        </div>

        {/* Voucher + Buyer */}
        <div className="bg-white p-3 border border-border rounded-sm shadow-sm">
          <div className="grid grid-cols-3 gap-3">
            <div>
              <Label className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wider">Voucher No.</Label>
              <Input value={voucherNumber} onChange={(e) => setVoucherNumber(e.target.value)} className="mt-1 h-8 text-sm font-medium" data-testid="voucher-input" />
            </div>
            <div>
              <Label className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wider">Buyer Name</Label>
              <Input value={buyerName} onChange={(e) => setBuyerName(e.target.value)} className="mt-1 h-8 text-sm" placeholder="Customer name" data-testid="buyer-name-input" />
            </div>
            <div>
              <Label className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wider">Buyer Mobile</Label>
              <Input value={buyerMobile} onChange={(e) => setBuyerMobile(e.target.value)} className="mt-1 h-8 text-sm" placeholder="Mobile" data-testid="buyer-mobile-input" />
            </div>
          </div>
          <div className="mt-2">
            <Label className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wider">Buyer Address</Label>
            <Input value={buyerAddress} onChange={(e) => setBuyerAddress(e.target.value)} className="mt-1 h-8 text-sm" placeholder="Customer address" data-testid="buyer-address-input" />
          </div>
        </div>

        {/* ── Area/Room Partition Management ── */}
        <div className="bg-white p-3 border border-border rounded-sm shadow-sm">
          <div className="flex items-center gap-2 flex-wrap">
            <Label className="text-[10px] font-semibold text-gray-500 uppercase tracking-wider whitespace-nowrap">Area / Room:</Label>
            {areas.map(area => (
              <div key={area.id} className="flex items-center gap-0.5">
                {editingAreaName === area.id ? (
                  <Input
                    autoFocus
                    value={tempAreaName}
                    onChange={(e) => setTempAreaName(e.target.value)}
                    onBlur={() => { if (tempAreaName.trim()) renameArea(area.id, tempAreaName.trim()); else setEditingAreaName(null); }}
                    onKeyDown={(e) => { if (e.key === 'Enter' && tempAreaName.trim()) renameArea(area.id, tempAreaName.trim()); if (e.key === 'Escape') setEditingAreaName(null); }}
                    className="h-7 text-xs w-24 px-1 font-semibold"
                    data-testid={`area-name-input-${area.id}`}
                  />
                ) : (
                  <button
                    onClick={() => setActiveAreaId(area.id)}
                    onDoubleClick={() => { setEditingAreaName(area.id); setTempAreaName(area.name); }}
                    className={`h-7 px-3 rounded text-xs font-semibold border transition-all ${
                      activeAreaId === area.id
                        ? 'bg-[#0066FF] text-white border-[#0066FF] shadow-sm'
                        : 'bg-white text-gray-700 border-gray-300 hover:border-[#0066FF] hover:text-[#0066FF]'
                    }`}
                    data-testid={`area-tab-${area.id}`}
                    title="Click to select, double-click to rename"
                  >
                    {area.name}
                  </button>
                )}
                {areas.length > 1 && (
                  <button onClick={() => removeArea(area.id)} className="text-red-400 hover:text-red-600 p-0.5" title="Remove area" data-testid={`remove-area-${area.id}`}>
                    <Trash size={12} />
                  </button>
                )}
              </div>
            ))}
            <Button onClick={addArea} variant="outline" size="sm" className="h-7 text-xs" data-testid="add-area-button">
              <Plus size={12} className="mr-1" /> Add Area
            </Button>
            <span className="text-[9px] text-muted-foreground ml-2 italic">Double-click area name to rename</span>
          </div>
          <div className="mt-1 text-[10px] text-muted-foreground">
            Editing selections for: <span className="font-bold text-[#0066FF]">{activeArea.name}</span> — Brand changes will only affect items in this area
          </div>
        </div>

        {/* Selection Panel - Collapsible */}
        <div className="border border-border rounded-sm shadow-sm overflow-hidden">
          <button
            onClick={() => setShowSelections(!showSelections)}
            className="w-full bg-[#111827] text-white px-4 py-2 flex items-center justify-between text-xs font-semibold uppercase tracking-wider"
            data-testid="toggle-selections"
          >
            <span>Brand & Category Selections</span>
            {showSelections ? <CaretUp size={14} /> : <CaretDown size={14} />}
          </button>
          {showSelections && (
            <div className="p-3 bg-[#F8F9FB] space-y-3">
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
                {categoryConfigs.map((cat, catIdx) => (
                  <div key={`cat-${cat.id}-${activeAreaId}`} className="relative group">
                    {/* Category management icons */}
                    <div className="absolute top-1 right-1 flex gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity z-10">
                      {catIdx > 0 && <button onClick={() => moveCategoryUp(cat.id)} className="p-0.5 text-gray-400 hover:text-blue-600" title="Move up" data-testid={`move-up-cat-${cat.id}`}><ArrowUp size={11} /></button>}
                      {catIdx < categoryConfigs.length - 1 && <button onClick={() => moveCategoryDown(cat.id)} className="p-0.5 text-gray-400 hover:text-blue-600" title="Move down" data-testid={`move-down-cat-${cat.id}`}><ArrowDown size={11} /></button>}
                      <button onClick={() => { setEditingCatId(cat.id); setEditingCatName(cat.name); }} className="p-0.5 text-gray-400 hover:text-blue-600" title="Rename" data-testid={`rename-cat-${cat.id}`}><PencilSimple size={11} /></button>
                      <button onClick={() => setConfiguringCatId(configuringCatId === cat.id ? null : cat.id)} className="p-0.5 text-gray-400 hover:text-purple-600" title="Configure fields" data-testid={`config-cat-${cat.id}`}><GearSix size={11} /></button>
                      <button onClick={() => setConfirmDeleteCatId(cat.id)} className="p-0.5 text-gray-400 hover:text-red-600" title="Remove category" data-testid={`remove-cat-${cat.id}`}><X size={11} /></button>
                    </div>
                    {/* Delete confirmation overlay */}
                    {confirmDeleteCatId === cat.id && (
                      <div className="absolute inset-0 bg-white/95 z-20 flex flex-col items-center justify-center rounded-sm border border-red-300 shadow-md">
                        <div className="text-[11px] font-semibold text-red-700 mb-1">Delete "{cat.name}"?</div>
                        <div className="text-[9px] text-gray-500 mb-2">This cannot be undone</div>
                        <div className="flex gap-2">
                          <button onClick={() => removeCategory(cat.id)} className="px-3 py-1 bg-red-600 text-white text-[10px] font-medium rounded hover:bg-red-700" data-testid={`confirm-delete-cat-${cat.id}`}>Delete</button>
                          <button onClick={() => setConfirmDeleteCatId(null)} className="px-3 py-1 bg-gray-100 text-gray-700 text-[10px] font-medium rounded hover:bg-gray-200" data-testid={`cancel-delete-cat-${cat.id}`}>Cancel</button>
                        </div>
                      </div>
                    )}
                    {/* Inline name editing */}
                    {editingCatId === cat.id ? (
                      <div className="border border-border rounded-sm p-3 bg-white">
                        <input
                          autoFocus value={editingCatName}
                          onChange={(e) => setEditingCatName(e.target.value)}
                          onBlur={() => renameCategoryConfirm(cat.id)}
                          onKeyDown={(e) => { if (e.key === 'Enter') renameCategoryConfirm(cat.id); if (e.key === 'Escape') setEditingCatId(null); }}
                          className="text-[10px] font-semibold uppercase tracking-wider border-b border-blue-400 outline-none w-full mb-2 pb-1"
                          style={{ color: cat.color }}
                          data-testid={`cat-name-input-${cat.id}`}
                        />
                      </div>
                    ) : (
                      <DropdownGroup
                        label={cat.name}
                        fields={buildFieldsForCategory(cat)}
                        state={sel[cat.id] || buildEmptySelection(cat)}
                        onChange={(v) => updateSel(cat.id, v)}
                        color={cat.color}
                      />
                    )}
                    {/* Field configuration popover */}
                    {configuringCatId === cat.id && (
                      <div className="absolute top-full left-0 mt-1 bg-white border border-border rounded shadow-lg p-2 z-20 min-w-[160px]" data-testid={`cat-fields-config-${cat.id}`}>
                        <div className="text-[9px] font-semibold text-gray-500 uppercase mb-1">Fields</div>
                        {AVAILABLE_FIELDS.map(f => (
                          <label key={f.key} className="flex items-center gap-1.5 text-[11px] py-0.5 cursor-pointer">
                            <input
                              type="checkbox"
                              checked={cat.fields.includes(f.key)}
                              disabled={f.key === 'brand'}
                              onChange={() => toggleCategoryField(cat.id, f.key)}
                              className="rounded border-gray-300"
                            />
                            <span className={f.key === 'brand' ? 'text-gray-400' : ''}>{f.label}</span>
                          </label>
                        ))}
                        <button onClick={() => setConfiguringCatId(null)} className="mt-1 text-[10px] text-blue-600 hover:underline w-full text-center">Done</button>
                      </div>
                    )}
                  </div>
                ))}
                {/* Add Category Button */}
                <div className="border border-dashed border-gray-300 rounded-sm p-3 flex items-center justify-center min-h-[80px] hover:border-blue-400 hover:bg-blue-50/30 transition-colors cursor-pointer" onClick={() => setShowAddCategory(true)} data-testid="add-category-button">
                  <div className="text-center">
                    <Plus size={16} className="mx-auto text-gray-400 mb-1" />
                    <span className="text-[10px] text-gray-500 font-medium">Add Category</span>
                  </div>
                </div>
              </div>
              {/* Add Category Form */}
              {showAddCategory && (
                <div className="bg-white border border-blue-200 rounded p-3 space-y-2" data-testid="add-category-form">
                  <div className="text-[10px] font-semibold text-blue-700 uppercase">New Category</div>
                  <div className="flex gap-2 items-end flex-wrap">
                    <div>
                      <Label className="text-[9px] text-gray-500 uppercase">Display Name</Label>
                      <Input value={newCatName} onChange={(e) => setNewCatName(e.target.value)} placeholder="e.g. FAN" className="h-7 text-xs w-32" data-testid="new-cat-name" />
                    </div>
                    <div>
                      <Label className="text-[9px] text-gray-500 uppercase">Product Key (DB)</Label>
                      <Input value={newCatProductKey} onChange={(e) => setNewCatProductKey(e.target.value)} placeholder="e.g. FAN" className="h-7 text-xs w-32" data-testid="new-cat-product-key" />
                    </div>
                    <div>
                      <Label className="text-[9px] text-gray-500 uppercase">Fields</Label>
                      <div className="flex gap-2 mt-1">
                        {AVAILABLE_FIELDS.map(f => (
                          <label key={f.key} className="flex items-center gap-1 text-[10px]">
                            <input type="checkbox" checked={newCatFields.includes(f.key)} disabled={f.key === 'brand'} onChange={() => setNewCatFields(prev => prev.includes(f.key) ? prev.filter(x => x !== f.key) : [...prev, f.key])} className="rounded border-gray-300" />
                            {f.label}
                          </label>
                        ))}
                      </div>
                    </div>
                    <Button onClick={addCategory} size="sm" className="h-7 text-xs bg-blue-600 hover:bg-blue-700 text-white" data-testid="confirm-add-category">
                      <Plus size={12} className="mr-1" /> Add
                    </Button>
                    <Button onClick={() => setShowAddCategory(false)} variant="outline" size="sm" className="h-7 text-xs" data-testid="cancel-add-category">Cancel</Button>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Manual Accessory Entry */}
        <div className="bg-white p-3 border border-border rounded-sm shadow-sm">
          <Label className="text-[10px] font-semibold text-gray-500 uppercase tracking-wider mb-2 block">General Accessories (Manual Entry - adds to quotation only)</Label>
          <div className="grid grid-cols-12 gap-2 items-end">
            <div className="col-span-4">
              <Label className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wider">Description</Label>
              <Input value={manualDesc} onChange={(e) => setManualDesc(e.target.value)} placeholder="SADDLE, SCREW, FAN ROD..." className="mt-1 h-8 text-sm" data-testid="manual-desc-input" />
            </div>
            <div className="col-span-2">
              <Label className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wider">Unit</Label>
              <select value={manualUnit} onChange={(e) => setManualUnit(e.target.value)} className="mt-1 h-8 w-full border border-border rounded-sm px-2 text-xs bg-white" data-testid="manual-unit-select">
                <option value="NOS">NOS</option>
                <option value="MTRS">MTRS</option>
                <option value="KG">KG</option>
                <option value="SET">SET</option>
                <option value="BOX">BOX</option>
                <option value="PKT">PKT</option>
                <option value="ROLL">ROLL</option>
                <option value="LOT">LOT</option>
              </select>
            </div>
            <div className="col-span-1">
              <Label className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wider">Qty</Label>
              <Input type="number" min="1" value={manualQty} onChange={(e) => setManualQty(e.target.value)} className="mt-1 h-8 text-sm" data-testid="manual-qty-input" />
            </div>
            <div className="col-span-2">
              <Label className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wider">MRP</Label>
              <Input type="number" min="0" step="0.01" value={manualMrp} onChange={(e) => setManualMrp(e.target.value)} placeholder="0.00" className="mt-1 h-8 text-sm" data-testid="manual-mrp-input" />
            </div>
            <div className="col-span-3">
              <Button onClick={handleAddManualItem} className="w-full h-8 bg-gray-700 hover:bg-gray-800 text-white text-xs" data-testid="add-manual-button">
                <Plus size={14} className="mr-1" /> Add Accessory
              </Button>
            </div>
          </div>
        </div>

        {/* Create New Product (saves to database) */}
        <div className="bg-white p-3 border border-amber-200 rounded-sm shadow-sm">
          <Label className="text-[10px] font-semibold text-amber-700 uppercase tracking-wider mb-2 block">Create New Product (saves to database permanently)</Label>
          <div className="flex gap-1.5 items-end flex-wrap">
            <div className="w-[80px]">
              <Label className="text-[10px] font-semibold text-muted-foreground uppercase">Code</Label>
              <Input list="dl-codes" value={newProd.code} onChange={(e) => setNewProd({...newProd, code: e.target.value})} placeholder="6A1W" className="mt-1 h-7 text-xs font-mono" data-testid="new-prod-code" />
              <datalist id="dl-codes">{distinctValues.codes.map(v => <option key={v} value={v} />)}</datalist>
            </div>
            <div className="w-[110px]">
              <Label className="text-[10px] font-semibold text-muted-foreground uppercase">Description</Label>
              <Input list="dl-descs" value={newProd.description} onChange={(e) => setNewProd({...newProd, description: e.target.value})} placeholder="6A 1WAY" className="mt-1 h-7 text-xs" data-testid="new-prod-desc" />
              <datalist id="dl-descs">{distinctValues.descriptions.map(v => <option key={v} value={v} />)}</datalist>
            </div>
            <div className="w-[80px]">
              <Label className="text-[10px] font-semibold text-muted-foreground uppercase">Cat. No.</Label>
              <Input value={newProd.catalog_number} onChange={(e) => setNewProd({...newProd, catalog_number: e.target.value})} placeholder="681100" className="mt-1 h-7 text-xs font-mono" data-testid="new-prod-catalog" />
            </div>
            <div className="w-[90px]">
              <Label className="text-[10px] font-semibold text-muted-foreground uppercase">Category</Label>
              <select value={newProd.product} onChange={(e) => setNewProd({...newProd, product: e.target.value})} className="mt-1 h-7 w-full border border-border rounded-sm px-1 text-[10px] bg-white" data-testid="new-prod-category">
                {[...new Set([...categoryConfigs.map(c => c.productKey), 'ACCESSORY'])].map(c => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
            <div className="w-[60px]">
              <Label className="text-[10px] font-semibold text-muted-foreground uppercase">Unit</Label>
              <select value={newProd.unit} onChange={(e) => setNewProd({...newProd, unit: e.target.value})} className="mt-1 h-7 w-full border border-border rounded-sm px-1 text-[10px] bg-white" data-testid="new-prod-unit">
                {['NOS','MTRS','KG','SET','BOX','PKT','ROLL','LOT'].map(u => <option key={u} value={u}>{u}</option>)}
              </select>
            </div>
            <div className="w-[65px]">
              <Label className="text-[10px] font-semibold text-muted-foreground uppercase">MRP</Label>
              <Input type="number" min="0" step="0.01" value={newProd.mrp} onChange={(e) => setNewProd({...newProd, mrp: e.target.value})} className="mt-1 h-7 text-xs" data-testid="new-prod-mrp" />
            </div>
            <div className="w-[85px]">
              <Label className="text-[10px] font-semibold text-muted-foreground uppercase">Brand</Label>
              <Input list="dl-brands" value={newProd.brand} onChange={(e) => setNewProd({...newProd, brand: e.target.value})} placeholder="LEG" className="mt-1 h-7 text-xs" data-testid="new-prod-brand" />
              <datalist id="dl-brands">{distinctValues.brands.map(v => <option key={v} value={v} />)}</datalist>
            </div>
            <div className="w-[80px]">
              <Label className="text-[10px] font-semibold text-muted-foreground uppercase">Sub Brand</Label>
              <Input list="dl-types" value={newProd.type} onChange={(e) => setNewProd({...newProd, type: e.target.value})} placeholder="ALLZY" className="mt-1 h-7 text-xs" data-testid="new-prod-type" />
              <datalist id="dl-types">{distinctValues.types.map(v => <option key={v} value={v} />)}</datalist>
            </div>
            <div className="w-[70px]">
              <Label className="text-[10px] font-semibold text-muted-foreground uppercase">Type</Label>
              <Input list="dl-subtypes" value={newProd.switch_subtype} onChange={(e) => setNewProd({...newProd, switch_subtype: e.target.value})} placeholder="G-CLICK" className="mt-1 h-7 text-xs" data-testid="new-prod-switch-subtype" />
              <datalist id="dl-subtypes">{distinctValues.switch_subtypes.map(v => <option key={v} value={v} />)}</datalist>
            </div>
            <div className="w-[70px]">
              <Label className="text-[10px] font-semibold text-muted-foreground uppercase">Color</Label>
              <Input list="dl-colors" value={newProd.color} onChange={(e) => setNewProd({...newProd, color: e.target.value})} placeholder="WHITE" className="mt-1 h-7 text-xs" data-testid="new-prod-color" />
              <datalist id="dl-colors">{distinctValues.colors.map(v => <option key={v} value={v} />)}</datalist>
            </div>
          </div>
          <div className="mt-2 flex justify-end gap-2">
            <Button onClick={() => createProduct(false)} variant="outline" className="h-7 text-xs" data-testid="create-product-button">
              <Plus size={14} className="mr-1" /> Create Product
            </Button>
            <Button onClick={() => createProduct(true)} className="h-7 text-xs bg-amber-600 hover:bg-amber-700 text-white" data-testid="create-and-add-button">
              <Plus size={14} className="mr-1" /> Create & Add
            </Button>
          </div>
        </div>

        {/* Code Entry - Add Item to Quotation */}
        <div className="bg-white p-3 border border-border rounded-sm shadow-sm">
          <div className="grid grid-cols-12 gap-2 items-end">
            <div className="col-span-5 sm:col-span-4 relative">
              <Label className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wider">Code</Label>
              <Input
                ref={codeInputRef}
                value={codeInput}
                onChange={(e) => handleCodeChange(e.target.value)}
                onKeyDown={handleCodeKeyDown}
                onFocus={() => { if (codeInput && suggestions.length > 0) setShowSuggestions(true); }}
                onBlur={() => { setTimeout(() => setShowSuggestions(false), 150); }}
                placeholder="6A1W, 1MP, 6ASP, 6WPNL..."
                className="mt-1 h-8 text-sm font-mono font-medium"
                autoComplete="off"
                data-testid="code-input"
              />
              {showSuggestions && (
                <div
                  ref={suggestionsRef}
                  className="absolute z-50 left-0 right-0 mt-0.5 bg-white border border-border rounded-sm shadow-lg max-h-48 overflow-y-auto"
                  data-testid="code-suggestions"
                >
                  {suggestions.map((item, i) => (
                    <div
                      key={item.code}
                      onMouseDown={() => selectSuggestion(item.code)}
                      className={`px-3 py-1.5 text-xs cursor-pointer flex items-center gap-2 ${
                        i === highlightIdx ? 'bg-[#0066FF] text-white' : 'hover:bg-[#F0F4FF]'
                      }`}
                      data-testid={`suggestion-${item.code}`}
                    >
                      <span className="font-mono font-semibold shrink-0">{item.code}</span>
                      <span className={`${i === highlightIdx ? 'text-blue-100' : 'text-muted-foreground'}`}>{item.description}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
            <div className="col-span-2">
              <Label className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wider">Qty</Label>
              <Input type="number" min="1" value={qtyInput} onChange={(e) => setQtyInput(e.target.value)} onKeyDown={handleCodeKeyDown} className="mt-1 h-8 text-sm" data-testid="qty-input" />
            </div>
            <div className="col-span-2">
              <Label className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wider">Disc %</Label>
              <Input type="number" min="0" max="100" value={discountInput} onChange={(e) => setDiscountInput(e.target.value)} onKeyDown={handleCodeKeyDown} className="mt-1 h-8 text-sm" data-testid="discount-input" />
            </div>
            <div className="col-span-3 sm:col-span-4">
              <Button onClick={handleAddItem} className="w-full h-8 bg-[#0066FF] hover:bg-[#0052CC] text-white text-xs" data-testid="add-item-button">
                <Plus size={14} className="mr-1" /> Add Item
              </Button>
            </div>
          </div>
        </div>

        {/* Quotation Table */}
        <div className="bg-white border border-border rounded-sm shadow-sm overflow-hidden">
          <div className="border-b border-border px-3 py-2 flex items-center justify-between">
            <h3 className="text-sm font-semibold">Quotation Items</h3>
            {items.length > 0 && (
              <div className="flex items-center gap-2">
                <Button variant="outline" size="sm" onClick={updateAllPrices} disabled={isUpdatingPrices} className="h-7 text-xs border-amber-300 text-amber-700 hover:bg-amber-50" data-testid="update-prices-button">
                  <ArrowsClockwise size={14} className={`mr-1 ${isUpdatingPrices ? 'animate-spin' : ''}`} /> {isUpdatingPrices ? 'Updating...' : 'Update Prices'}
                </Button>
                <Button variant="outline" size="sm" onClick={handlePrint} className="h-7 text-xs" data-testid="print-button">
                  <Printer size={14} className="mr-1" /> Print
                </Button>
                <Button variant="outline" size="sm" onClick={handleConsolidatedPrint} className="h-7 text-xs border-emerald-300 text-emerald-700 hover:bg-emerald-50" data-testid="consolidated-print-button">
                  <ListChecks size={14} className="mr-1" /> Material List
                </Button>
              </div>
            )}
          </div>
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="bg-[#111827]">
                  <TableHead className="font-medium text-white text-[11px] w-[35px]">Sr.</TableHead>
                  <TableHead className="font-medium text-white text-[11px] w-[55px]">Code</TableHead>
                  <TableHead className="font-medium text-white text-[11px] w-[65px]">Cat. No.</TableHead>
                  <TableHead className="font-medium text-white text-[11px]">Description</TableHead>
                  <TableHead className="font-medium text-white text-[11px] w-[75px]">Brand</TableHead>
                  <TableHead className="font-medium text-white text-[11px] w-[65px]">Sub Brand</TableHead>
                  <TableHead className="font-medium text-white text-[11px] w-[60px]">Type</TableHead>
                  <TableHead className="font-medium text-white text-[11px] w-[55px]">Color</TableHead>
                  <TableHead className="font-medium text-white text-[11px] w-[40px]">Cat</TableHead>
                  <TableHead className="font-medium text-white text-[11px] w-[35px]">Unit</TableHead>
                  <TableHead className="font-medium text-white text-[11px] w-[50px]">Qty</TableHead>
                  <TableHead className="font-medium text-white text-[11px] w-[60px]">MRP</TableHead>
                  <TableHead className="font-medium text-white text-[11px] w-[50px]">Disc%</TableHead>
                  <TableHead className="font-medium text-white text-[11px] w-[70px]">Total</TableHead>
                  <TableHead className="font-medium text-white text-[11px] w-[30px]"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {items.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={16} className="text-center text-muted-foreground py-10 text-xs" data-testid="empty-quotation">
                      Select an area, set brands above, then enter product codes to add items
                    </TableCell>
                  </TableRow>
                ) : (
                  (() => {
                    // Group items by area, maintaining insertion order
                    const areaOrder = [];
                    const areaGroups = {};
                    items.forEach(item => {
                      const a = item.area || 'GENERAL';
                      if (!areaGroups[a]) { areaGroups[a] = []; areaOrder.push(a); }
                      areaGroups[a].push(item);
                    });
                    let globalIdx = 0;
                    return areaOrder.flatMap(areaName => {
                      const areaItems = areaGroups[areaName];
                      const areaSubTotal = areaItems.reduce((s, it) => s + it.amount, 0);
                      return [
                        // Area header row
                        <TableRow key={`hdr-${areaName}`} className="bg-[#E8EDFB]">
                          <TableCell colSpan={16} className="py-1.5 px-3">
                            <span className="text-xs font-bold text-[#0066FF] uppercase tracking-wider">{areaName}</span>
                            <span className="text-[10px] text-muted-foreground ml-3">({areaItems.length} items — Sub Total: {areaSubTotal.toFixed(2)})</span>
                          </TableCell>
                        </TableRow>,
                        // Items in this area
                        ...areaItems.map((item) => {
                          const idx = globalIdx++;
                          const itemIdx = items.indexOf(item);
                          return [
                            <TableRow key={idx} className={idx % 2 === 0 ? 'bg-white' : 'bg-[#F8F9FB]'}>
                              <TableCell className="text-xs">{item.sr_no}</TableCell>
                              <TableCell className="text-xs font-mono font-semibold text-[#0066FF]">
                                {editingItemIdx === itemIdx ? (
                                  <Input
                                    autoFocus
                                    value={editCode}
                                    onChange={(e) => setEditCode(e.target.value.toUpperCase())}
                                    onKeyDown={(e) => { if (e.key === 'Enter') handleEditItem(itemIdx); if (e.key === 'Escape') setEditingItemIdx(null); }}
                                    onBlur={() => { if (editCode.trim() && editCode.trim().toUpperCase() !== items[itemIdx].code) handleEditItem(itemIdx); else setEditingItemIdx(null); }}
                                    className="h-6 text-xs w-20 px-1 font-mono border-blue-400"
                                    data-testid={`edit-code-${idx}`}
                                  />
                                ) : item.code}
                              </TableCell>
                              <TableCell className="text-xs font-mono text-muted-foreground">{item.catalog_number || '-'}</TableCell>
                              <TableCell className="text-xs">{item.description}</TableCell>
                              <TableCell className="text-xs font-medium">{item.brand}</TableCell>
                              <TableCell className="text-xs text-muted-foreground">{item.product_type}</TableCell>
                              <TableCell className="text-xs text-muted-foreground">{item.switch_subtype || '-'}</TableCell>
                              <TableCell className="text-xs" data-testid={`item-color-${idx}`}>{item.color}</TableCell>
                              <TableCell>
                                <span className={`px-1 py-0.5 rounded text-[9px] font-semibold ${
                                  item.product === 'SWITCH' ? 'bg-blue-100 text-blue-700' :
                                  item.product === 'PLATE' ? 'bg-amber-100 text-amber-700' :
                                  item.product === 'WIRE' ? 'bg-green-100 text-green-700' :
                                  item.product === 'PROJECT WIRE' ? 'bg-emerald-100 text-emerald-700' :
                                  item.product === 'MCB' || item.product === 'ELCB' ? 'bg-purple-100 text-purple-700' :
                                  item.product === 'PIPE' ? 'bg-teal-100 text-teal-700' :
                                  item.product?.startsWith('LED') ? 'bg-red-100 text-red-700' :
                                  item.product === 'SMPS' ? 'bg-pink-100 text-pink-700' :
                                  'bg-gray-100 text-gray-700'
                                }`}>{item.product}</span>
                              </TableCell>
                              <TableCell className="text-[10px] text-muted-foreground">{item.unit || 'NOS'}</TableCell>
                              <TableCell>
                                <Input type="number" min="1" value={item.quantity} onChange={(e) => updateItemField(itemIdx, 'quantity', e.target.value)}
                                  className="h-6 text-xs w-14 px-1" data-testid={`item-qty-${idx}`} />
                              </TableCell>
                              <TableCell className="text-xs font-medium">{item.mrp.toFixed(2)}</TableCell>
                              <TableCell>
                                <Input type="number" min="0" max="100" value={item.discount} onChange={(e) => updateItemField(itemIdx, 'discount', e.target.value)}
                                  className="h-6 text-xs w-14 px-1" data-testid={`item-disc-${idx}`} />
                              </TableCell>
                              <TableCell className="text-xs font-semibold">{item.amount.toFixed(2)}</TableCell>
                              <TableCell>
                                <div className="flex items-center gap-0.5">
                                  <button onClick={() => startEditItem(itemIdx)} className="text-blue-500 hover:text-blue-700 p-0.5" title="Edit item code" data-testid={`edit-item-${idx}`}>
                                    <PencilSimple size={12} />
                                  </button>
                                  <button onClick={() => { setInsertAfterIdx(insertAfterIdx === itemIdx ? null : itemIdx); setInsertCode(''); setInsertQty('1'); setInsertDisc('0'); setEditingItemIdx(null); }} className="text-green-500 hover:text-green-700 p-0.5" title="Insert item below" data-testid={`insert-after-${idx}`}>
                                    <Plus size={12} />
                                  </button>
                                  <button onClick={() => removeItem(itemIdx)} className="text-red-500 hover:text-red-700 p-0.5" data-testid={`remove-item-${idx}`}>
                                    <Trash size={12} />
                                  </button>
                                </div>
                              </TableCell>
                            </TableRow>,
                            insertAfterIdx === itemIdx && (
                              <TableRow key={`insert-${idx}`} className="bg-green-50 border-y border-green-300">
                                <TableCell colSpan={2} className="py-1.5 px-2">
                                  <div className="text-[9px] text-green-700 font-semibold mb-0.5">INSERT AFTER Sr. {item.sr_no}</div>
                                </TableCell>
                                <TableCell colSpan={8} className="py-1.5 px-1">
                                  <div className="flex items-center gap-1.5">
                                    <Input
                                      ref={insertCodeRef}
                                      autoFocus
                                      value={insertCode}
                                      onChange={(e) => setInsertCode(e.target.value.toUpperCase())}
                                      onKeyDown={(e) => { if (e.key === 'Enter') handleInsertItem(itemIdx); if (e.key === 'Escape') setInsertAfterIdx(null); }}
                                      placeholder="Code"
                                      className="h-6 text-xs w-24 px-1 font-mono border-green-400"
                                      data-testid={`insert-code-${idx}`}
                                    />
                                    <Input type="number" min="1" value={insertQty} onChange={(e) => setInsertQty(e.target.value)} placeholder="Qty" className="h-6 text-xs w-12 px-1 border-green-400" data-testid={`insert-qty-${idx}`} />
                                    <Input type="number" min="0" max="100" value={insertDisc} onChange={(e) => setInsertDisc(e.target.value)} placeholder="Disc%" className="h-6 text-xs w-12 px-1 border-green-400" data-testid={`insert-disc-${idx}`} />
                                    <Button onClick={() => handleInsertItem(itemIdx)} size="sm" className="h-6 text-[10px] bg-green-600 hover:bg-green-700 text-white px-2" data-testid={`insert-confirm-${idx}`}>
                                      Insert
                                    </Button>
                                    <button onClick={() => setInsertAfterIdx(null)} className="text-gray-400 hover:text-gray-600 p-0.5"><X size={12} /></button>
                                  </div>
                                </TableCell>
                                <TableCell colSpan={5}></TableCell>
                              </TableRow>
                            ),
                          ];
                        }).flat().filter(Boolean),
                      ];
                    });
                  })()
                )}
                {items.length > 0 && (
                  <>
                    <TableRow className="bg-[#F0F2F5] border-t border-gray-300">
                      <TableCell colSpan={10} className="text-right text-xs font-semibold text-gray-600">TOTAL QTY</TableCell>
                      <TableCell className="text-xs font-bold text-gray-800" data-testid="total-qty">{items.reduce((s, i) => s + (parseInt(i.quantity) || 0), 0)}</TableCell>
                      <TableCell colSpan={2} className="text-right text-xs font-semibold text-gray-600">SUB TOTAL</TableCell>
                      <TableCell className="text-xs font-bold text-gray-800">{items.reduce((s, i) => s + i.amount, 0).toFixed(2)}</TableCell>
                      <TableCell></TableCell>
                    </TableRow>
                    <TableRow className="bg-[#111827]">
                      <TableCell colSpan={13} className="text-right text-white font-semibold text-xs">GRAND TOTAL (Rounded Up)</TableCell>
                      <TableCell className="text-white font-bold text-sm" data-testid="grand-total">{grandTotal.toFixed(2)}</TableCell>
                      <TableCell></TableCell>
                    </TableRow>
                  </>
                )}
              </TableBody>
            </Table>
          </div>
          {items.length > 0 && (
            <div className="px-3 py-2 border-t border-border flex justify-start gap-2">
              <Button variant="outline" size="sm" className="h-7 text-xs" onClick={resetForm} data-testid="new-quotation-button">New</Button>
              {editingQuotationId && (
                <Button variant="outline" size="sm" className="h-7 text-xs border-amber-400 text-amber-700 hover:bg-amber-50" onClick={duplicateQuotation} data-testid="duplicate-quotation-button">
                  <Copy size={14} className="mr-1" /> Duplicate
                </Button>
              )}
              <Button size="sm" className="h-7 text-xs bg-[#0066FF] hover:bg-[#0052CC] text-white" onClick={saveQuotation} data-testid="save-quotation-button">
                {editingQuotationId ? 'Update Quotation' : 'Save Quotation'}
              </Button>
              {editingQuotationId && (
                <Button size="sm" className="h-7 text-xs bg-[#25D366] hover:bg-[#1DA851] text-white" onClick={shareWhatsApp} data-testid="whatsapp-share-button">
                  <WhatsappLogo size={14} weight="fill" className="mr-1" /> WhatsApp
                </Button>
              )}
            </div>
          )}
        </div>

        {/* Post-Save Dialog */}
        {showPostSave && (
          <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center" data-testid="post-save-dialog">
            <div className="bg-white rounded-md shadow-xl p-5 w-[340px] text-center space-y-4">
              <div className="text-lg font-semibold text-green-700">Quotation Saved!</div>
              <p className="text-sm text-muted-foreground">What would you like to do next?</p>
              <div className="flex flex-wrap gap-3 justify-center">
                <Button size="sm" className="h-9 px-5 bg-[#111827] hover:bg-[#1f2937] text-white" onClick={() => { setShowPostSave(false); handlePrint(); }} data-testid="post-save-print">
                  <Printer size={16} className="mr-1.5" /> Print
                </Button>
                <Button size="sm" className="h-9 px-5 bg-emerald-600 hover:bg-emerald-700 text-white" onClick={() => { setShowPostSave(false); handleConsolidatedPrint(); }} data-testid="post-save-consolidated-print">
                  <ListChecks size={16} className="mr-1.5" /> Material List
                </Button>
                <Button size="sm" className="h-9 px-5 bg-[#25D366] hover:bg-[#1DA851] text-white" onClick={() => { setShowPostSave(false); shareWhatsApp(); }} data-testid="post-save-whatsapp">
                  <WhatsappLogo size={16} weight="fill" className="mr-1.5" /> WhatsApp
                </Button>
              </div>
              <button className="text-xs text-muted-foreground hover:text-foreground underline" onClick={() => { setShowPostSave(false); resetForm(); }} data-testid="post-save-close">
                New Quotation
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
